import React from 'react';
import { Box, Breadcrumbs, Container, Link, Typography } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from './Header';
import Footer from './Footer';
import ToolSeoSections from './ToolSeoSections';
import StructuredData from './StructuredData';
import AdSensePlaceholder from './AdSensePlaceholder';
import seoData from '../seo/toolSeoData.json';

const byRoute = new Map(seoData.tools.map((tool) => [tool.route, tool]));

/**
 * Shell for every non-home route. On tool routes it also owns the page's SEO head:
 * <title>, meta description and canonical come from src/seo/toolSeoData.json — the
 * same values scripts/inject-seo.js bakes into the prerendered HTML — so the head
 * Googlebot sees after rendering JS matches the static one. Tool components must
 * not set their own title/description (a deeper Helmet would override these).
 *
 * Ad placement rules: never above the main content, never two units back-to-back,
 * and the second unit only where long-form content separates it from the first.
 */
export default function ToolPageLayout({ children, ads = true }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const tool = pathname === '/' ? null : byRoute.get(pathname.replace(/\/+$/, '') || pathname);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {tool && (
        <>
          <Helmet>
            <title>{`${tool.title} – TG Edit-Pdf`}</title>
            <meta name="description" content={tool.description} />
          </Helmet>
          <StructuredData name={tool.title} description={tool.description} path={tool.route} />
        </>
      )}
      <Header />
      <Box component="main" sx={{ flex: 1, pt: { xs: 8, md: 10 } }}>
        {/* Visible breadcrumb matching the BreadcrumbList JSON-LD */}
        {tool && (
          <Container maxWidth="md" sx={{ pt: 2 }}>
            <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: '0.85rem' }}>
              <Link component={RouterLink} to="/" underline="hover" color="text.secondary" fontSize="inherit">
                {t('nav.home')}
              </Link>
              <Typography color="text.primary" fontSize="inherit">{tool.title}</Typography>
            </Breadcrumbs>
          </Container>
        )}
        {children}
        {ads && (
          <Container maxWidth="md">
            <AdSensePlaceholder slot="below-tool" sx={{ mt: 4 }} />
          </Container>
        )}
        <ToolSeoSections />
        {ads && tool && (
          <Container maxWidth="md">
            <AdSensePlaceholder slot="multiplex" format="multiplex" sx={{ mt: 2, mb: 2 }} />
          </Container>
        )}
      </Box>
      <Footer />
    </Box>
  );
}
